// Integrations panel. Secret fields are MASKED in the response body
// (e.g. "••••1234"). We never try to edit that mask in-place — instead
// the admin types a new value into the empty "Replace token" input, and
// we only send that field if they actually typed something. Untouched
// means the stored secret is kept intact by the backend.
//
// Token handling state (new-token-text + cleared-flag) is owned by the
// parent dialog so the save button can inspect + reset it.
import React from 'react';
import {
  SelectField, SwitchField, TextField,
} from './fields';
import {
  CALENDAR_PROVIDERS,
  FIRECRAWL_FREQS,
  PREFERRED_AI_PROVIDERS,
  SECRET_UNCHANGED,
  STORAGE_PROVIDERS,
} from './constants';

// Build the PATCH body for integrations. `tokenState` is the parent's
// { newToken, cleared } shape; untouched -> SECRET_UNCHANGED sentinel.
export function buildIntegrationsPatch(value, tokenState) {
  const { newToken = '', cleared = false } = tokenState || {};
  return {
    retell: value.retell,
    ai: value.ai,
    firecrawl: value.firecrawl,
    storage: value.storage,
    calendar: value.calendar,
    twilio: {
      twilio_enabled: value.twilio.twilio_enabled,
      twilio_account_sid: value.twilio.twilio_account_sid,
      twilio_phone_number: value.twilio.twilio_phone_number,
      twilio_auth_token: cleared
        ? ''
        : newToken.length > 0
          ? newToken
          : SECRET_UNCHANGED,
    },
  };
}

export default function IntegrationsSection({ value, onChange, tokenState, setTokenState }) {
  const set = (provider) => (patch) =>
    onChange({ ...value, [provider]: { ...value[provider], ...patch } });

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h4 className="text-sm font-semibold">Retell (voice agent)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField
            label="Agent ID"
            value={value.retell.retell_agent_id}
            onChange={(v) => set('retell')({ retell_agent_id: v })}
          />
          <TextField
            label="Phone number"
            value={value.retell.retell_phone_number}
            onChange={(v) => set('retell')({ retell_phone_number: v })}
          />
        </div>
        <SwitchField
          label="Test call enabled"
          description="Required before the 'Test call' button will do anything."
          value={value.retell.test_call_enabled}
          onChange={(v) => set('retell')({ test_call_enabled: v })}
        />
      </section>

      <section className="space-y-3">
        <h4 className="text-sm font-semibold">Twilio (SMS / fallback)</h4>
        <SwitchField
          label="Twilio enabled"
          value={value.twilio.twilio_enabled}
          onChange={(v) => set('twilio')({ twilio_enabled: v })}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField
            label="Account SID"
            value={value.twilio.twilio_account_sid}
            onChange={(v) => set('twilio')({ twilio_account_sid: v })}
          />
          <TextField
            label="Phone number"
            value={value.twilio.twilio_phone_number}
            onChange={(v) => set('twilio')({ twilio_phone_number: v })}
          />
        </div>
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">
            Current token: <span className="font-mono">{value.twilio.twilio_auth_token_masked || '—'}</span>
          </div>
          <TextField
            label="Replace auth token (leave blank to keep current)"
            value={tokenState.newToken}
            onChange={(v) => setTokenState({ ...tokenState, newToken: v })}
            type="password"
            placeholder="•••••• (optional)"
            disabled={tokenState.cleared}
          />
          <SwitchField
            label="Clear stored token instead"
            description="Removes the token from storage; Twilio calls will fail until re-entered."
            value={tokenState.cleared}
            onChange={(v) => setTokenState({ ...tokenState, cleared: v })}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h4 className="text-sm font-semibold">AI providers</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SwitchField
            label="OpenAI enabled"
            value={value.ai.openai_enabled}
            onChange={(v) => set('ai')({ openai_enabled: v })}
          />
          <SwitchField
            label="Anthropic enabled"
            value={value.ai.anthropic_enabled}
            onChange={(v) => set('ai')({ anthropic_enabled: v })}
          />
          <SelectField
            label="Preferred provider"
            value={value.ai.preferred_ai_provider}
            onChange={(v) => set('ai')({ preferred_ai_provider: v })}
            options={PREFERRED_AI_PROVIDERS}
          />
        </div>
        <TextField
          label="Preferred model"
          value={value.ai.preferred_model}
          onChange={(v) => set('ai')({ preferred_model: v })}
          placeholder="gpt-4o-mini / claude-3-5-sonnet"
        />
      </section>

      <section className="space-y-3">
        <h4 className="text-sm font-semibold">Firecrawl (inventory)</h4>
        <SwitchField
          label="Firecrawl enabled"
          value={value.firecrawl.firecrawl_enabled}
          onChange={(v) => set('firecrawl')({ firecrawl_enabled: v })}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField
            label="URL to crawl"
            value={value.firecrawl.firecrawl_url}
            onChange={(v) => set('firecrawl')({ firecrawl_url: v })}
          />
          <SelectField
            label="Frequency"
            value={value.firecrawl.firecrawl_frequency}
            onChange={(v) => set('firecrawl')({ firecrawl_frequency: v })}
            options={FIRECRAWL_FREQS}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h4 className="text-sm font-semibold">Storage (recordings / exports)</h4>
        <SwitchField
          label="Storage enabled"
          value={value.storage.storage_enabled}
          onChange={(v) => set('storage')({ storage_enabled: v })}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SelectField
            label="Provider"
            value={value.storage.storage_provider}
            onChange={(v) => set('storage')({ storage_provider: v })}
            options={STORAGE_PROVIDERS}
          />
          <TextField
            label="Bucket"
            value={value.storage.storage_bucket}
            onChange={(v) => set('storage')({ storage_bucket: v })}
          />
          <TextField
            label="Path prefix"
            value={value.storage.storage_path_prefix}
            onChange={(v) => set('storage')({ storage_path_prefix: v })}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h4 className="text-sm font-semibold">Calendar</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SelectField
            label="Provider"
            value={value.calendar.calendar_provider}
            onChange={(v) => set('calendar')({ calendar_provider: v })}
            options={CALENDAR_PROVIDERS}
          />
          <SwitchField
            label="Calendar enabled"
            value={value.calendar.calendar_enabled}
            onChange={(v) => set('calendar')({ calendar_enabled: v })}
          />
        </div>
      </section>
    </div>
  );
}
